import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useBookingDraft } from '@/contexts/BookingDraftContext';
import { Plus, Mail, Phone, MessageCircle, MoreVertical, FileText, CalendarPlus } from 'lucide-react';
import { toast } from 'sonner';

export default function PatientQuickActions({ patient }) {
  const { createDraft } = useBookingDraft();
  const [open, setOpen] = useState(false);

  const handleNewBooking = () => {
    createDraft({
      patientMode: 'selected',
      patientId: patient.id,
      patient: {
        firstName: patient.firstName,
        lastName: patient.lastName,
        email: patient.email,
        phone: patient.phone
      },
      siteKey: patient.siteKey
    });
    setOpen(false);
  };

  const handleSendMedicalForm = () => {
    // In a real app this would trigger an API call to dispatch the form
    toast.success('Medical Form link sent via SMS/Email');
    setOpen(false);
  };

  const actions = [
    { label: 'New Booking', icon: CalendarPlus, onClick: handleNewBooking, primary: true },
    { label: 'Email Patient', icon: Mail, onClick: () => window.location.href = `mailto:${patient.email}`, show: !!patient.email },
    { label: 'Call Patient', icon: Phone, onClick: () => window.location.href = `tel:${patient.phone}`, show: !!patient.phone },
    { label: 'WhatsApp', icon: MessageCircle, onClick: () => window.open(`https://wa.me/${patient.phone?.replace(/[^0-9]/g, '')}`, '_blank'), show: !!patient.phone },
    { label: 'Request Medical Form', icon: FileText, onClick: handleSendMedicalForm, show: true }
  ].filter(a => a.show !== false);

  return (
    <div className="relative">
      {/* Desktop Inline Actions */}
      <div className="hidden md:flex items-center gap-2">
        {actions.slice(0, 3).map((action, i) => {
          const Icon = action.icon;
          return (
            <button
              key={i}
              onClick={action.onClick}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border shadow-sm h-8 ${
                action.primary 
                  ? 'bg-stone-900 text-white border-stone-900 hover:bg-stone-800' 
                  : 'bg-white text-stone-700 border-stone-200 hover:bg-stone-50 hover:border-stone-300'
              }`}
            >
              <Icon className="w-3.5 h-3.5" /> {action.label}
            </button>
          );
        })}
        
        {actions.length > 3 && (
          <div className="relative">
            <button onClick={() => setOpen(!open)} className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-stone-200 bg-white text-stone-600 hover:bg-stone-50 transition-colors shadow-sm">
              <MoreVertical className="w-4 h-4" />
            </button>
            <AnimatePresence>
              {open && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
                  <motion.div
                    initial={{ opacity: 0, y: 5, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 5, scale: 0.95 }}
                    className="absolute right-0 top-full mt-1 w-48 bg-white border border-stone-200 rounded-xl shadow-xl z-50 overflow-hidden py-1"
                  >
                    {actions.slice(3).map((action, i) => {
                      const Icon = action.icon;
                      return (
                        <button
                          key={i}
                          onClick={action.onClick}
                          className="w-full text-left px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 flex items-center gap-2"
                        >
                          <Icon className="w-4 h-4 text-stone-400" /> {action.label}
                        </button>
                      );
                    })}
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Mobile Dropdown Only */}
      <div className="md:hidden relative">
        <button onClick={() => setOpen(!open)} className="h-9 inline-flex items-center gap-2 px-3 rounded-lg bg-stone-900 text-white hover:bg-stone-800 transition-colors shadow-sm text-sm font-medium">
          <Plus className="w-4 h-4" /> Actions
        </button>
        <AnimatePresence>
          {open && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
              <motion.div
                initial={{ opacity: 0, y: 5, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 5, scale: 0.95 }}
                className="absolute right-0 top-full mt-1 w-48 bg-white border border-stone-200 rounded-xl shadow-xl z-50 overflow-hidden py-1"
              >
                {actions.map((action, i) => {
                  const Icon = action.icon;
                  return (
                    <button
                      key={i}
                      onClick={action.onClick}
                      className="w-full text-left px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 flex items-center gap-2"
                    >
                      <Icon className="w-4 h-4 text-stone-400" /> {action.label}
                    </button>
                  );
                })}
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
